import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiPackage, FiDownload, FiPrinter, FiRotateCcw, FiSearch, FiBarChart2
} from 'react-icons/fi';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import useCurrency from '../../hooks/useCurrency';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { formatAmount } from '../../utils/documentNumbering';
import useReportMemory from '../../hooks/useReportMemory';

const POSTED_STATUSES = ['Posted', 'Partial', 'Paid'];

const LandedCostAnalysis = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('landed-cost-analysis');
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  // Base currency — labels follow company settings (PKR / AED / USD / etc.)
  const baseCurrency = activeCompany?.currencyCode || 'PKR';

  const [invoices, setInvoices] = useState(() => savedMem?.invoices ?? []);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(() => savedMem ? false : true);

  // Filters
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [vendorFilter, setVendorFilter] = useState(() => savedMem?.vendorFilter ?? 'all');
  const [itemFilter, setItemFilter] = useState(() => savedMem?.itemFilter ?? 'all');
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');
  const [view, setView] = useState(() => savedMem?.view ?? 'byInvoice'); // byInvoice | byItem | byCharge

  // ─── Load FY list, vendors, items, invoices ───
  const loadFYs = useCallback(async () => {
    if (!activeCompany?.id) return;
    const snap = await getDocs(query(
      collection(db, 'companies', activeCompany.id, 'financialYears'),
      orderBy('startDate', 'desc')
    ));
    const fys = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setFinancialYears(fys);
    if (!selectedFYId) setSelectedFYId(activeFinancialYear?.id || fys[0]?.id || '');
  }, [activeCompany, activeFinancialYear, selectedFYId]);

  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    const snap = await getDocs(query(
      collection(db, 'companies', activeCompany.id, 'vendors'),
      orderBy('vendorName')
    ));
    setVendors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    const snap = await getDocs(collection(db, 'companies', activeCompany.id, 'inventoryItems'));
    setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isDeleted !== true));
  }, [activeCompany]);

  const loadInvoices = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'companies', activeCompany.id, 'transactions'),
        where('tranTypeCode', '==', 'IPI'),
        where('financialYearId', '==', selectedFYId)
      ));
      let txs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Branch filter (client-side — no composite index needed)
      if (activeBranch && activeBranch.id !== 'all') {
        txs = txs.filter(t => t.branchId === activeBranch.id);
      }
      // Only posted/partial/paid show in landed cost report
      txs = txs.filter(t => POSTED_STATUSES.includes(t.status));
      setInvoices(txs);
    } catch (e) {
      console.error('Load IPI report error:', e);
    } finally { setLoading(false); }
  }, [activeCompany, selectedFYId, activeBranch]);

  useEffect(() => { loadFYs(); loadVendors(); loadItems(); }, [loadFYs, loadVendors, loadItems]);
  // Backward memory: on Back (snapshot present) skip the initial auto-load so we
  // reuse the restored invoices with 0 re-reads; still re-fetch on later FY changes.
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    loadInvoices();
  }, [loadInvoices]);

  // Backward memory: keep the snapshot in sync (filters are display-only over the
  // already-loaded invoices), so Back restores this exact view with 0 re-reads.
  useEffect(() => {
    if (loading) return;
    saveMemory({ invoices, selectedFYId, dateFrom, dateTo, vendorFilter, itemFilter, searchQuery, view });
  }, [loading, invoices, selectedFYId, dateFrom, dateTo, vendorFilter, itemFilter, searchQuery, view, saveMemory]);

  const resetFilters = () => {
    setDateFrom(''); setDateTo('');
    setVendorFilter('all'); setItemFilter('all');
    setSearchQuery('');
    setSelectedFYId(activeFinancialYear?.id || financialYears[0]?.id || '');
  };

  // ─── Apply filters ───
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      if (vendorFilter !== 'all' && inv.partyId !== vendorFilter) return false;
      if (dateFrom && inv.docDate < dateFrom) return false;
      if (dateTo && inv.docDate > dateTo) return false;
      if (itemFilter !== 'all') {
        const has = (inv.lines || []).some(l => l.itemId === itemFilter);
        if (!has) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matches = (inv.docNumber || '').toLowerCase().includes(q) ||
                        (inv.partyName || '').toLowerCase().includes(q) ||
                        (inv.gdNumber || '').toLowerCase().includes(q) ||
                        (inv.vendorInvoiceNo || '').toLowerCase().includes(q);
        if (!matches) return false;
      }
      return true;
    });
  }, [invoices, vendorFilter, dateFrom, dateTo, itemFilter, searchQuery]);

  // ─── KPI Cards ───
  const kpis = useMemo(() => {
    const totalImports = filteredInvoices.length;
    let foreignTotal = 0, baseTotal = 0, capTotal = 0, nonCapTotal = 0, invValue = 0, grandTotal = 0;
    filteredInvoices.forEach(inv => {
      foreignTotal += inv.foreignSubtotal || 0;
      baseTotal += inv.baseSubtotal || 0;
      capTotal += inv.capitalizedTotal || 0;
      nonCapTotal += inv.nonCapitalizedTotal || 0;
      invValue += inv.inventoryValue || 0;
      grandTotal += inv.totalAmount || 0;
    });
    const markupPct = baseTotal > 0 ? ((capTotal / baseTotal) * 100) : 0;
    return { totalImports, foreignTotal, baseTotal, capTotal, nonCapTotal, invValue, grandTotal, markupPct };
  }, [filteredInvoices]);

  // ─── By Item pivot ───
  const byItemRows = useMemo(() => {
    const map = {};
    filteredInvoices.forEach(inv => {
      (inv.lines || []).forEach(l => {
        if (itemFilter !== 'all' && l.itemId !== itemFilter) return;
        if (!l.itemId) return;
        if (!map[l.itemId]) {
          map[l.itemId] = {
            itemId: l.itemId,
            itemCode: l.itemCode || '',
            itemName: l.itemName || '',
            totalQty: 0,
            totalForeign: 0,
            totalBase: 0,
            totalCapitalized: 0,
            totalLanded: 0,
            sumLandedQty: 0,    // Σ(landedUnitCost × qty) for weighted avg
            importCount: 0,
            lastDate: ''
          };
        }
        const r = map[l.itemId];
        const qty = Number(l.quantity) || 0;
        r.totalQty += qty;
        r.totalForeign += Number(l.foreignTotal) || 0;
        r.totalBase += Number(l.baseTotal) || 0;
        r.totalCapitalized += Number(l.capitalizedAllocated || l.capitalizedTotal || 0);
        r.totalLanded += Number(l.landedTotal) || 0;
        r.sumLandedQty += (Number(l.landedUnitCost) || 0) * qty;
        r.importCount += 1;
        if (!r.lastDate || (inv.docDate || '') > r.lastDate) r.lastDate = inv.docDate || '';
      });
    });
    return Object.values(map).map(r => ({
      ...r,
      avgLandedUnitCost: r.totalQty > 0 ? r.sumLandedQty / r.totalQty : 0,
      avgForeignUnit: r.totalQty > 0 ? r.totalForeign / r.totalQty : 0,
      avgBaseUnit: r.totalQty > 0 ? r.totalBase / r.totalQty : 0
    })).sort((a, b) => b.totalLanded - a.totalLanded);
  }, [filteredInvoices, itemFilter]);

  // ─── By Charge pivot ───
  const byChargeRows = useMemo(() => {
    const map = {};
    filteredInvoices.forEach(inv => {
      (inv.landedCharges || inv.importCharges || []).forEach(c => {
        const key = c.chargeName || '(unnamed)';
        if (!map[key]) {
          map[key] = {
            chargeName: key,
            count: 0,
            totalCapitalized: 0,
            totalNonCapitalized: 0,
            totalAmount: 0
          };
        }
        const amt = Number(c.chargeAmount) || 0;
        map[key].count += 1;
        map[key].totalAmount += amt;
        if (c.capitalize) map[key].totalCapitalized += amt;
        else map[key].totalNonCapitalized += amt;
      });
    });
    const grandTotal = Object.values(map).reduce((s, r) => s + r.totalAmount, 0);
    return Object.values(map).map(r => ({
      ...r,
      pctOfAll: grandTotal > 0 ? (r.totalAmount / grandTotal) * 100 : 0
    })).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredInvoices]);

  // ─── Exports ───
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    let sheetData;

    if (view === 'byInvoice') {
      sheetData = filteredInvoices.map(inv => ({
        'Doc #': inv.docNumber,
        'Date': inv.docDate,
        'Vendor': inv.partyName,
        'Currency': inv.foreignCurrency,
        'Rate': inv.exchangeRate,
        'GD #': inv.gdNumber || '',
        'Foreign Subtotal': inv.foreignSubtotal || 0,
        [`${baseCurrency} Subtotal`]: inv.baseSubtotal || 0,
        'Capitalized': inv.capitalizedTotal || 0,
        'Non-Capitalized': inv.nonCapitalizedTotal || 0,
        [`Inventory Value (${baseCurrency})`]: inv.inventoryValue || 0,
        [`Grand Total Owed (${baseCurrency})`]: inv.totalAmount || 0,
        'Status': inv.status
      }));
    } else if (view === 'byItem') {
      sheetData = byItemRows.map(r => ({
        'Item Code': r.itemCode,
        'Item Name': r.itemName,
        '# Imports': r.importCount,
        'Total Qty': r.totalQty,
        'Avg Foreign Unit': r.avgForeignUnit,
        [`Avg ${baseCurrency} Unit`]: r.avgBaseUnit,
        'Avg Landed Unit Cost': r.avgLandedUnitCost,
        'Total Foreign': r.totalForeign,
        [`Total ${baseCurrency}`]: r.totalBase,
        'Total Capitalized': r.totalCapitalized,
        'Total Landed Value': r.totalLanded,
        'Last Imported': r.lastDate
      }));
    } else {
      sheetData = byChargeRows.map(r => ({
        'Charge': r.chargeName,
        '# Times Used': r.count,
        'Capitalized Total': r.totalCapitalized,
        'Non-Capitalized Total': r.totalNonCapitalized,
        'Total Amount': r.totalAmount,
        '% of All Charges': r.pctOfAll.toFixed(2) + '%'
      }));
    }

    const ws = XLSX.utils.json_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, 'Landed Cost');
    XLSX.writeFile(wb, `LandedCostAnalysis_${view}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const printReport = () => window.print();

  // ═══════════════════════════════════════════════════════════
  return (
    <PageLayout pageTitle="Landed Cost Analysis" breadcrumb="Reports / Purchase / Landed Cost Analysis">
      {/* Filters */}
      <FiltersCard>
        <FilterGrid>
          <FilterItem>
            <FilterLabel>Financial Year</FilterLabel>
            <FilterSelect value={selectedFYId} onChange={(e) => setSelectedFYId(e.target.value)}>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode}</option>
              ))}
            </FilterSelect>
          </FilterItem>
          <FilterItem>
            <FilterLabel>From</FilterLabel>
            <FilterInput type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </FilterItem>
          <FilterItem>
            <FilterLabel>To</FilterLabel>
            <FilterInput type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </FilterItem>
          <FilterItem>
            <FilterLabel>Vendor</FilterLabel>
            <FilterSelect value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}>
              <option value="all">All Vendors</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.vendorName}</option>)}
            </FilterSelect>
          </FilterItem>
          <FilterItem>
            <FilterLabel>Item</FilterLabel>
            <FilterSelect value={itemFilter} onChange={(e) => setItemFilter(e.target.value)}>
              <option value="all">All Items</option>
              {items.map(i => <option key={i.id} value={i.id}>{i.itemCode} — {i.itemName}</option>)}
            </FilterSelect>
          </FilterItem>
          <FilterItem style={{ minWidth: 220 }}>
            <FilterLabel>Search</FilterLabel>
            <FilterInputWrap>
              <FiSearch />
              <FilterInput type="text" placeholder="Doc#, GD#, vendor..."
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </FilterInputWrap>
          </FilterItem>
          <FilterItem>
            <FilterLabel>&nbsp;</FilterLabel>
            <ResetBtn onClick={resetFilters}><FiRotateCcw /> Reset</ResetBtn>
          </FilterItem>
        </FilterGrid>
      </FiltersCard>

      {/* KPI Cards */}
      <KPIGrid>
        <KPICard>
          <KPILabel># of Imports</KPILabel>
          <KPIValue>{kpis.totalImports}</KPIValue>
        </KPICard>
        <KPICard>
          <KPILabel>Foreign Cost (orig. currency)</KPILabel>
          <KPIValue>{kpis.foreignTotal.toLocaleString()}</KPIValue>
        </KPICard>
        <KPICard>
          <KPILabel>Foreign Cost in {baseCurrency}</KPILabel>
          <KPIValue>{formatAmount(kpis.baseTotal)}</KPIValue>
        </KPICard>
        <KPICard $accent>
          <KPILabel>Capitalized Charges</KPILabel>
          <KPIValue>{formatAmount(kpis.capTotal)}</KPIValue>
          <KPISub>+{kpis.markupPct.toFixed(1)}% on foreign</KPISub>
        </KPICard>
        <KPICard>
          <KPILabel>Non-Capitalized (claimable)</KPILabel>
          <KPIValue>{formatAmount(kpis.nonCapTotal)}</KPIValue>
        </KPICard>
        <KPICard $highlight>
          <KPILabel>Total Inventory Value</KPILabel>
          <KPIValue>{formatAmount(kpis.invValue)}</KPIValue>
        </KPICard>
      </KPIGrid>

      {/* View tabs + Export */}
      <ToolbarRow>
        <Tabs>
          <Tab $active={view === 'byInvoice'} onClick={() => setView('byInvoice')}>
            <FiPackage /> By Invoice ({filteredInvoices.length})
          </Tab>
          <Tab $active={view === 'byItem'} onClick={() => setView('byItem')}>
            <FiBarChart2 /> By Item ({byItemRows.length})
          </Tab>
          <Tab $active={view === 'byCharge'} onClick={() => setView('byCharge')}>
            <FiBarChart2 /> By Charge ({byChargeRows.length})
          </Tab>
        </Tabs>
        <div style={{ display: 'flex', gap: spacing.sm }}>
          <ExportBtn onClick={exportToExcel}><FiDownload /> Excel</ExportBtn>
          <ExportBtn onClick={printReport}><FiPrinter /> Print</ExportBtn>
        </div>
      </ToolbarRow>

      {loading ? (
        <Loading>Loading...</Loading>
      ) : (
        <TableCard>
          {view === 'byInvoice' && (
            <Table>
              <thead>
                <tr>
                  <Th>Doc #</Th>
                  <Th>Date</Th>
                  <Th>Vendor</Th>
                  <Th>Currency</Th>
                  <Th>GD #</Th>
                  <Th style={{ textAlign: 'right' }}>Foreign</Th>
                  <Th style={{ textAlign: 'right' }}>{baseCurrency} Cost</Th>
                  <Th style={{ textAlign: 'right' }}>+ Capitalized</Th>
                  <Th style={{ textAlign: 'right' }}>+ Non-Cap</Th>
                  <Th style={{ textAlign: 'right' }}>Inventory Value</Th>
                  <Th style={{ textAlign: 'right' }}>Total Owed</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 ? (
                  <tr><Td colSpan="12" style={{ textAlign: 'center', color: colors.text.secondary, padding: spacing.xl }}>
                    No Import Purchase Invoices match your filters.
                  </Td></tr>
                ) : filteredInvoices.map(inv => (
                  <Tr key={inv.id}>
                    <Td><strong onClick={() => navigateToTransaction(navigate, inv.tranTypeCode || 'IPI', inv.id)}
                      title="Open import invoice" style={{ color: colors.primary.main, cursor: 'pointer' }}>{inv.docNumber}</strong></Td>
                    <Td>{inv.docDate}</Td>
                    <Td>{inv.partyName || '-'}</Td>
                    <Td>
                      <CurrencyTag>{inv.foreignCurrency}</CurrencyTag>
                      <small style={{ color: colors.text.secondary, marginLeft: 4 }}>@ {inv.exchangeRate}</small>
                    </Td>
                    <Td>{inv.gdNumber || '-'}</Td>
                    <Td style={{ textAlign: 'right' }}>{(inv.foreignSubtotal || 0).toLocaleString()}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(inv.baseSubtotal || 0)}</Td>
                    <Td style={{ textAlign: 'right', color: colors.primary.main }}>+{formatAmount(inv.capitalizedTotal || 0)}</Td>
                    <Td style={{ textAlign: 'right', color: colors.info.dark }}>+{formatAmount(inv.nonCapitalizedTotal || 0)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(inv.inventoryValue || 0)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(inv.totalAmount || 0)}</Td>
                    <Td>{inv.status}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}

          {view === 'byItem' && (
            <Table>
              <thead>
                <tr>
                  <Th>Item Code</Th>
                  <Th>Item Name</Th>
                  <Th style={{ textAlign: 'center' }}># Imports</Th>
                  <Th style={{ textAlign: 'right' }}>Total Qty</Th>
                  <Th style={{ textAlign: 'right' }}>Avg Foreign / Unit</Th>
                  <Th style={{ textAlign: 'right' }}>Avg {baseCurrency} / Unit</Th>
                  <Th style={{ textAlign: 'right' }}>Avg Landed Unit Cost</Th>
                  <Th style={{ textAlign: 'right' }}>Total Landed Value</Th>
                  <Th>Last Imported</Th>
                </tr>
              </thead>
              <tbody>
                {byItemRows.length === 0 ? (
                  <tr><Td colSpan="9" style={{ textAlign: 'center', color: colors.text.secondary, padding: spacing.xl }}>
                    No item data.
                  </Td></tr>
                ) : byItemRows.map(r => (
                  <Tr key={r.itemId}>
                    <Td><strong>{r.itemCode}</strong></Td>
                    <Td>{r.itemName}</Td>
                    <Td style={{ textAlign: 'center' }}>{r.importCount}</Td>
                    <Td style={{ textAlign: 'right' }}>{r.totalQty.toLocaleString()}</Td>
                    <Td style={{ textAlign: 'right' }}>{r.avgForeignUnit.toFixed(2)}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(r.avgBaseUnit)}</Td>
                    <Td style={{ textAlign: 'right', color: colors.success.dark, fontWeight: 700 }}>
                      {formatAmount(r.avgLandedUnitCost)}
                    </Td>
                    <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(r.totalLanded)}</Td>
                    <Td>{r.lastDate}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}

          {view === 'byCharge' && (
            <Table>
              <thead>
                <tr>
                  <Th>Charge Name</Th>
                  <Th style={{ textAlign: 'center' }}># Times Used</Th>
                  <Th style={{ textAlign: 'right' }}>Capitalized</Th>
                  <Th style={{ textAlign: 'right' }}>Non-Capitalized</Th>
                  <Th style={{ textAlign: 'right' }}>Total Amount</Th>
                  <Th style={{ textAlign: 'right' }}>% of All Charges</Th>
                </tr>
              </thead>
              <tbody>
                {byChargeRows.length === 0 ? (
                  <tr><Td colSpan="6" style={{ textAlign: 'center', color: colors.text.secondary, padding: spacing.xl }}>
                    No charge data.
                  </Td></tr>
                ) : byChargeRows.map((r, i) => (
                  <Tr key={i}>
                    <Td><strong>{r.chargeName}</strong></Td>
                    <Td style={{ textAlign: 'center' }}>{r.count}</Td>
                    <Td style={{ textAlign: 'right', color: colors.primary.main }}>{formatAmount(r.totalCapitalized)}</Td>
                    <Td style={{ textAlign: 'right', color: colors.info.dark }}>{formatAmount(r.totalNonCapitalized)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 700 }}>{formatAmount(r.totalAmount)}</Td>
                    <Td style={{ textAlign: 'right' }}>
                      <PctBar><PctFill style={{ width: `${Math.min(100, r.pctOfAll)}%` }} /></PctBar>
                      <span style={{ fontSize: 12, marginLeft: 6 }}>{r.pctOfAll.toFixed(1)}%</span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableCard>
      )}
    </PageLayout>
  );
};

// ─── Styles ───
const FiltersCard = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.md};
  box-shadow: ${shadows.sm};
  margin-bottom: ${spacing.md};
`;

const FilterGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: ${spacing.md};
  align-items: flex-end;
`;

const FilterItem = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  min-width: 140px; flex: 1;
`;

const FilterLabel = styled.label`
  font-size: 11px; font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase; letter-spacing: 0.5px;
`;

const FilterInputWrap = styled.div`
  display: flex; align-items: center; gap: ${spacing.sm};
  padding: 0 ${spacing.sm};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary};

  input { border: none; outline: none; background: transparent; flex: 1; padding: ${spacing.sm} 0; }
`;

const FilterInput = styled.input`
  padding: ${spacing.sm};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const FilterSelect = styled.select`
  padding: ${spacing.sm};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  background: ${colors.background.default};
`;

const ResetBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.neutral[100]};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  cursor: pointer;
  &:hover { background: ${colors.neutral[200]}; }
`;

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${spacing.md};
  margin-bottom: ${spacing.md};
`;

const KPICard = styled.div`
  background: ${props => props.$highlight ? colors.primary.main : colors.background.default};
  color: ${props => props.$highlight ? 'white' : colors.text.primary};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.md};
  box-shadow: ${shadows.sm};
  border-left: 3px solid ${props =>
    props.$highlight ? colors.primary.dark :
    props.$accent ? colors.primary.main : colors.border.light};
`;

const KPILabel = styled.div`
  font-size: 11px; font-weight: 600;
  opacity: 0.85;
  text-transform: uppercase; letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const KPIValue = styled.div`
  font-size: ${typography.fontSize.xl};
  font-weight: 700;
  font-family: monospace;
`;

const KPISub = styled.div`
  font-size: 11px; margin-top: 4px;
  opacity: 0.7;
`;

const ToolbarRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: ${spacing.md}; gap: ${spacing.md}; flex-wrap: wrap;
`;

const Tabs = styled.div`
  display: flex; gap: ${spacing.xs};
  background: ${colors.neutral[100]};
  border-radius: ${borderRadius.sm};
  padding: 3px;
`;

const Tab = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.$active ? colors.background.default : 'transparent'};
  color: ${props => props.$active ? colors.primary.main : colors.text.secondary};
  border: none;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  font-weight: ${props => props.$active ? 600 : 500};
  cursor: pointer;
  box-shadow: ${props => props.$active ? shadows.sm : 'none'};
  &:hover { color: ${colors.primary.main}; }
`;

const ExportBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${colors.primary.dark}; }
`;

const TableCard = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.sm};
  overflow: auto;
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; min-width: 900px;
`;

const Th = styled.th`
  padding: ${spacing.md};
  text-align: left;
  background: ${colors.neutral[50]};
  color: ${colors.text.secondary};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.border.light};
  white-space: nowrap;
`;

const Tr = styled.tr`
  &:hover { background: ${colors.neutral[50]}; }
`;

const Td = styled.td`
  padding: ${spacing.md};
  font-size: ${typography.fontSize.sm};
  border-bottom: 1px solid ${colors.neutral[100]};
`;

const CurrencyTag = styled.span`
  display: inline-block; padding: 2px 8px;
  background: ${colors.info.main}20; color: ${colors.info.dark};
  border-radius: ${borderRadius.sm}; font-size: 11px; font-weight: 600;
`;

const PctBar = styled.div`
  display: inline-block;
  width: 80px; height: 8px;
  background: ${colors.neutral[200]};
  border-radius: 4px; overflow: hidden;
  vertical-align: middle;
`;

const PctFill = styled.div`
  height: 100%;
  background: ${colors.primary.main};
`;

const Loading = styled.div`
  padding: ${spacing.xxl}; text-align: center;
  color: ${colors.text.secondary};
`;

export default LandedCostAnalysis;
